import EscPosEncoder from '@/lib/print/EscPosEncoder';
import {
  IEscposPrinterSettings,
  IZplPrinterSettings,
  injectZplOffsets,
  normalizeEscposSettings,
  normalizeZplSettings,
  zplOffsetCommands,
} from '@/lib/print/printer-settings';
import { renderQr, utf8ByteLength } from '@/lib/print/qr-render';
import { PrinterCutMode } from '@/stores/printing-v2.config';

/**
 * Test documents for the V2 printing UI (wizard StepTest, printer card
 * "Imprimir prueba", edit-modal Probar buttons and the Ayuda codepage
 * cycle). Printed-paper copy is deliberately Spanish-only (support
 * material, not UI copy).
 */

export type ITestTicketOptions = Partial<IEscposPrinterSettings>;

/** Codepages offered by the "Imprime símbolos raros" cycle, in print order. */
export const CODEPAGE_CYCLE = ['cp858', 'cp437', 'windows1252'] as const;

/** Line with every character class a Spanish ticket needs. */
const CHARSET_SAMPLE = 'áéíóú ÁÉÍÓÚ ñÑ ¿¡ $ € ° 1234567890';

/** 50-digit ruler for visual chars-per-line calibration. */
const CALIBRATION_RULER = '12345678901234567890123456789012345678901234567890';

function encoderFor(settings: IEscposPrinterSettings): EscPosEncoder {
  return new EscPosEncoder({
    paperWidthMm: settings.paperWidthMm,
    charsPerLineNormal: settings.charsPerLineNormal,
    charsPerLineSmall: settings.charsPerLineSmall,
    model: settings.model,
  });
}

/** Width-calibration block (-pos parity): ruler + space-between rows. */
function appendWidthCalibrationBlock(
  encoder: EscPosEncoder,
  settings: IEscposPrinterSettings
): void {
  const normalLabel = settings.charsPerLineNormal
    ? String(settings.charsPerLineNormal)
    : 'auto';
  const smallLabel = settings.charsPerLineSmall
    ? String(settings.charsPerLineSmall)
    : 'auto';
  encoder
    .align('left')
    .newline()
    .boldLine('BLOQUE DE CALIBRACION DE ANCHO')
    .line(`Ancho lógico normal/small: ${normalLabel}/${smallLabel}`)
    .separator()
    .line(CALIBRATION_RULER)
    .keyValueSpaceBetween('Clave corta:', '12345')
    .keyValueSpaceBetween(
      'Clave de ejemplo suficientemente larga para ajuste:',
      '$123.456,78'
    )
    .separator()
    .newline();
}

export function buildTestTicket(options: ITestTicketOptions): number[] {
  const settings = normalizeEscposSettings(options);
  const encoder = encoderFor(settings)
    .initialize()
    .codepage(settings.codepage)
    .align('center')
    .bold(true)
    .line('Prueba de impresión')
    .bold(false)
    .line(CHARSET_SAMPLE)
    .line(`Papel: ${settings.paperWidthMm} mm`)
    .line(`Codificación: ${settings.codepage}`)
    .line('https://queresto.com');

  appendWidthCalibrationBlock(encoder, settings);

  encoder
    .align('center')
    .newline(settings.feedLines ?? 3)
    .cut(settings.cutType, settings.cutMode);

  return Array.from(new Uint8Array(encoder.encode()));
}

/**
 * One numbered sample for the codepage cycle: the user picks the number that
 * printed readable Spanish characters.
 */
export function buildCodepageSample(
  sampleNumber: number,
  codepage: string,
  cutMode: PrinterCutMode = 'modern'
): number[] {
  const encoder = new EscPosEncoder()
    .initialize()
    .codepage(codepage)
    .align('center')
    .bold(true)
    .setSize(2, 2)
    .line(`MUESTRA ${sampleNumber}`)
    .setSize(1, 1)
    .bold(false)
    .line(CHARSET_SAMPLE)
    .newline()
    .cut('partial', cutMode);

  return Array.from(new Uint8Array(encoder.encode()));
}

/**
 * Long-QR (AFIP-like) test: validates the selected QR render mode against
 * real hardware limits. Async because the image path rasterizes a QR.
 */
export async function buildLongQrTestDoc(
  options: ITestTicketOptions
): Promise<number[]> {
  const settings = normalizeEscposSettings(options);
  const now = new Date();
  const dateStr = now.toLocaleDateString('es-AR');
  const timeStr = now.toLocaleTimeString('es-AR');

  const afipLikePayload = {
    ver: 1,
    fecha: now.toISOString().slice(0, 10),
    cuit: 20304050607,
    ptoVta: 1,
    tipoCmp: 6,
    nroCmp: 12345678,
    importe: 123456.78,
    moneda: 'PES',
    ctz: 1,
    tipoDocRec: 96,
    nroDocRec: 12345678,
    tipoCodAut: 'E',
    codAut: 68063411473670,
    impIVA: 21000.12,
  };
  // JSON of the payload is pure ASCII — btoa is safe.
  const qrUrl = `https://www.afip.gob.ar/fe/qr/?p=${btoa(
    JSON.stringify(afipLikePayload)
  )}`;

  const encoder = encoderFor(settings)
    .initialize()
    .codepage(settings.codepage)
    .align('center')
    .boldLine('PRUEBA QR LARGO')
    .separator()
    .newline()
    .align('left')
    .keyValue('Fecha:', dateStr)
    .keyValue('Hora:', timeStr)
    .keyValue('Modo QR:', settings.qrMode)
    .keyValue('Bytes QR:', String(utf8ByteLength(qrUrl)))
    .keyValue('Len URL:', String(qrUrl.length))
    .newline()
    .align('center');

  await renderQr(encoder, qrUrl, {
    qrMode: settings.qrMode,
    paperWidthMm: settings.paperWidthMm,
  });

  encoder
    .newline()
    .align('left')
    .line("Si falla o imprime texto, elija 'Imagen' o 'Auto'.")
    .newline(settings.feedLines ?? 3)
    .cut(settings.cutType, settings.cutMode);

  return Array.from(new Uint8Array(encoder.encode()));
}

/**
 * One accented sample line per codepage in ONE buffer (single job — per-line
 * jobs could interleave with a concurrent auto-print on the same printer).
 */
export function buildCodepageMegaTest(
  codepages: readonly string[],
  options: ITestTicketOptions
): number[] {
  const settings = normalizeEscposSettings(options);
  let encoder = encoderFor(settings)
    .initialize()
    .align('center')
    .line('Prueba de impresión');

  for (const codepage of codepages) {
    encoder = encoder.codepage(codepage).line(codepage + ' - ÁáÉéÍíÓóÚúÑñ$€');
  }

  encoder.newline(settings.feedLines ?? 3).cut(settings.cutType, settings.cutMode);

  return Array.from(new Uint8Array(encoder.encode()));
}

/**
 * Fixed ZPL test label for zpl-format printers. `withOffsets` must mirror
 * how REAL labels print on this printer (triplet only with explicit
 * per-printer settings) — a test print must never leave persisted ^LH/^LS/^LT
 * state on a printer whose real labels won't reset it.
 */
export function buildTestZplLabel(
  zpl?: Partial<IZplPrinterSettings>,
  withOffsets = true
): string {
  const settings = normalizeZplSettings(zpl);
  const label = [
    '^XA',
    '^CI28',
    '^CF0,36',
    '^FO30,30^FDPrueba de impresion^FS',
    '^CF0,28',
    '^FO30,80^FDQueResto^FS',
    '^FO30,120^FD1234567890^FS',
    '^XZ',
  ].join('\n');
  return withOffsets ? injectZplOffsets(label, settings) : label;
}

/**
 * ZPL calibration label: a dot-precise ^GB border filling the configured
 * label dimensions (clipped edges = width/height too large) with the spec
 * summary centered inside. Raw ZPL, no jszpl.
 */
export function buildZplCalibrationDoc(zpl: IZplPrinterSettings): string {
  const settings = normalizeZplSettings(zpl);
  const widthDots = Math.round(settings.widthMm * settings.dpmm);
  const heightDots = Math.round(settings.heightMm * settings.dpmm);
  const borderThickness = Math.max(
    2,
    Math.min(6, Math.round(settings.dpmm * 0.3))
  );

  // jszpl font F metrics (26x13 dots at the 8dpmm base), scaled by density.
  const scale = settings.dpmm / 8;
  const fontHeight = Math.round(26 * scale);
  const fontWidth = Math.round(13 * scale);
  const lineStep = fontHeight + Math.round(4 * scale);

  const specLines = [
    `${settings.widthMm}x${settings.heightMm}mm @${settings.dpmm}dpmm`,
    `PW=${widthDots} dots`,
    `ofs=${settings.offsetXmm},${settings.offsetYmm}mm`,
  ];

  const inset = borderThickness + 2;
  const contentHeight = specLines.length * lineStep;
  const firstLineY = Math.max(
    inset,
    Math.round((heightDots - contentHeight) / 2)
  );
  const lineFields = specLines.map(
    (text, index) =>
      `^FO0,${firstLineY + index * lineStep}` +
      `^AFN,${fontHeight},${fontWidth}` +
      `^FB${widthDots},1,0,C,0^FD${text}^FS`
  );

  return [
    '^XA',
    `^PW${widthDots}`,
    `^LL${heightDots}`,
    zplOffsetCommands(settings),
    `^FO0,0^GB${widthDots},${heightDots},${borderThickness},B,0^FS`,
    ...lineFields,
    '^XZ',
  ].join('\n');
}
